import React, { useEffect, useRef, useState } from 'react';
import { useTheme } from '../../context/ThemeContext';
import adsConfig from '../../config/adsConfig';
import './AdBanner.css';

/**
 * Componente para exibir anúncios AdSense
 * @param {Object} props - Propriedades do componente
 * @param {string} props.slot - ID do slot do anúncio AdSense
 * @param {string} props.format - Formato do anúncio (auto, rectangle, horizontal, etc)
 * @param {boolean} props.responsive - Se o anúncio deve ser responsivo
 * @param {string} props.className - Classes CSS adicionais
 * @param {string} props.type - Tipo de anúncio (in-article, feed, etc)
 * @param {string} props.position - Posição do anúncio (top, sidebar, content, etc)
 * @param {boolean} props.ignoreConfig - Se deve ignorar a configuração global e sempre tentar carregar
 */
const AdBanner = ({ 
  slot, 
  format = 'auto', 
  responsive = true, 
  className = '',
  type = '',
  position = '',
  ignoreConfig = false
}) => {
  const adRef = useRef(null);
  const [isAdLoaded, setIsAdLoaded] = useState(false);
  const [isAdBlocked, setIsAdBlocked] = useState(false);
  const [adLoadAttempted, setAdLoadAttempted] = useState(false);
  // Usando o valor diretamente da configuração sem precisar de estado local
  const showAd = ignoreConfig ? true : adsConfig.enabled;
  const { theme } = useTheme();

  useEffect(() => {
    // Se os anúncios estão desativados, não faça nada
    if (!showAd) {
      return;
    }

    // Função para detectar se o AdBlock está ativo
    const checkAdBlocker = () => {
      try {
        const testAd = document.createElement('div');
        testAd.innerHTML = '&nbsp;';
        testAd.className = 'adsbox';
        document.body.appendChild(testAd);
        
        window.setTimeout(() => {
          if (testAd.offsetHeight === 0) {
            setIsAdBlocked(true);
          }
          testAd.remove();
        }, 100);
      } catch (e) {
        console.error('Erro ao verificar AdBlock:', e);
      }
    };

    // Carrega os anúncios apenas se o script do AdSense estiver disponível
    const loadAd = () => {
      if (!adLoadAttempted && adRef.current) {
        setAdLoadAttempted(true);
        
        if (typeof window !== 'undefined' && window.adsbygoogle) {
          try {
            // Se o ad já foi carregado, não tenta carregar novamente
            if (!isAdLoaded) {
              (window.adsbygoogle = window.adsbygoogle || []).push({});
              setIsAdLoaded(true);
            }
          } catch (e) {
            console.error('Erro ao carregar anúncio:', e);
          }
        } else if (typeof window !== 'undefined') {
          // Se o AdSense não estiver disponível, definimos um timeout único
          const adTimeout = setTimeout(() => {
            if (window.adsbygoogle) {
              try {
                (window.adsbygoogle = window.adsbygoogle || []).push({});
                setIsAdLoaded(true);
              } catch (error) {
                console.warn('Não foi possível carregar o anúncio após timeout');
              }
            } else {
              console.log('AdSense não disponível após timeout');
            }
          }, adsConfig.adSenseLoadTimeout);
          
          return () => clearTimeout(adTimeout);
        }
      }
    };

    checkAdBlocker();

    // Implementa lazy loading para os anúncios
    if (typeof IntersectionObserver !== 'undefined' && adRef.current && !adLoadAttempted) {
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            loadAd();
            observer.disconnect();
          }
        },
        { threshold: 0.1 }
      );

      observer.observe(adRef.current);
      
      return () => {
        observer.disconnect();
      };
    } else {
      // Fallback para navegadores que não suportam IntersectionObserver
      loadAd();
    }
  }, [isAdLoaded, adLoadAttempted, showAd]);

  // Renderização do anúncio ou placeholder apropriado
  return (
    <div 
      className={`ad-banner ${position} ${className} ${isAdBlocked ? 'ad-blocked' : ''}`}
      data-ad-status={isAdLoaded ? 'loaded' : isAdBlocked ? 'blocked' : 'empty'}
    >
      <div className="ad-banner-label">Anúncio</div>
      
      {showAd ? (
        <>
          {isAdBlocked ? (
            <div className="ad-blocked-message">
              Detectamos que você está usando um bloqueador de anúncios. 
              Considere apoiar nosso site desativando o bloqueador.
            </div>
          ) : (
            <div 
              ref={adRef} 
              className="ad-banner-inner"
              style={{ display: isAdLoaded ? 'block' : 'none' }}
            >
              <ins
                className="adsbygoogle"
                style={{ display: 'block' }}
                data-ad-client={adsConfig.adClientId}
                data-ad-slot={slot}
                data-ad-format={format}
                data-full-width-responsive={responsive ? 'true' : 'false'}
                data-ad-type={type || null}
              ></ins>
            </div>
          )}
          
          {!isAdLoaded && !isAdBlocked && (
            <div className="ad-banner-placeholder">
              Carregando anúncio...
            </div>
          )}
        </>
      ) : (
        <div className="ad-banner-placeholder">
          Espaço reservado para anúncio
        </div>
      )}
    </div>
  );
};

export default AdBanner;